import { Info, X } from "lucide-react";
import { Close as PopoverClose } from "@radix-ui/react-popover";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { EnrollmentCalculationResult, MemberBreakdown } from "@shared/utils/enrollmentCalculator";
import { isSpouseRelationship, isChildRelationship } from "@shared/utils/enrollmentCalculator";
import {
  calculatePerPaycheckAmount,
  getPayCycleLabel,
  getPayCyclePeriodsPerYear,
  hasNonMonthlyPayCycle,
} from "@/lib/payrollUtils";

function usd(n: number | null | undefined): string {
  const v = typeof n === "number" ? n : parseFloat(String(n ?? 0));
  if (!Number.isFinite(v)) return "$0.00";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function tierLabel(tier?: string | null): string {
  return String(tier ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolve a display name for each calc member from the covered household. The
 * calc's members[] are ordered [employee, spouse?, ...children]; match the
 * non-employee rows to covered dependents by relationship class + age.
 */
function resolveNames(
  members: MemberBreakdown[],
  employee: any,
  coveredDependents: any[],
): string[] {
  const pool = [...(coveredDependents ?? [])];
  const take = (predicate: (d: any) => boolean, age?: number | null): string | null => {
    let idx = pool.findIndex((d) => predicate(d) && (age == null || Number(d.age ?? d.ageYears) === age));
    if (idx === -1) idx = pool.findIndex(predicate); // fall back to any matching relationship
    if (idx === -1) return null;
    const [d] = pool.splice(idx, 1);
    return `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim() || null;
  };
  let childIndex = 0;
  return members.map((m) => {
    if (m.memberType === "employee") {
      return `${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`.trim() || "Employee";
    }
    if (m.memberType === "spouse") {
      return take((d) => isSpouseRelationship(d.relationship), m.age) ?? "Spouse";
    }
    childIndex += 1;
    return take((d) => isChildRelationship(d.relationship), m.age) ?? `Child ${childIndex}`;
  });
}

type NeedsConfigReason = "contributions" | "coverage" | "both" | null;

function configMessage(reason: NeedsConfigReason): string {
  switch (reason) {
    case "contributions":
      return "needs employer contributions configured";
    case "coverage":
      return "needs its coverage amount configured";
    case "both":
      return "needs rates and contributions configured";
    default:
      return "needs rates configured";
  }
}

interface Props {
  result: EnrollmentCalculationResult;
  payCycle?: string | null;
  planName?: string | null;
  coverageTier?: string | null;
  employee?: any;
  coveredDependents?: any[];
  /** When set (the card's "Needs configuration" pill), show a config note instead of a $0 breakdown. */
  needsConfig?: NeedsConfigReason;
  /** True while the card's rates/contributions are still loading. */
  isLoading?: boolean;
}

/**
 * "Show the math" popover. The trigger is a circular info button (the premium
 * figure itself lives in the card). Renders one of three states from the
 * already-computed EnrollmentCalculationResult — member roster (age-rated),
 * itemized tier breakdown, or a fully-employer-paid note. Pure/presentational.
 */
export function PremiumBreakdownPopover({
  result,
  payCycle,
  planName,
  coverageTier,
  employee,
  coveredDependents,
  needsConfig = null,
  isLoading = false,
}: Props) {
  const members = result?.members ?? [];
  // Can't produce a meaningful breakdown: the card flagged a config gap, or
  // the calc returned nothing usable. Gated on !isLoading — an all-zero result
  // also happens while rates are still in flight, which must show the neutral
  // loading note below, never the amber config warning.
  const cannotCalculate =
    !isLoading &&
    (!!needsConfig ||
      members.length === 0 ||
      (result.totalMonthlyRate === 0 && result.employeePremium === 0 && result.employerPremium === 0));
  const employerPaid = result.employeePremium === 0 && result.employerPremium > 0;
  const isRoster = members.length > 1;
  const nonMonthly = hasNonMonthlyPayCycle(payCycle);
  const periods = getPayCyclePeriodsPerYear(payCycle);
  const perCycle = calculatePerPaycheckAmount(result.employeePremium, payCycle);
  const cycleLabel = getPayCycleLabel(payCycle);
  // Empty on monthly pay cycles, where the /mo figure already is the per-paycheck amount.
  const employerPerCycleText = nonMonthly
    ? ` · ${usd(calculatePerPaycheckAmount(result.employerPremium, payCycle))} per ${cycleLabel}`
    : "";

  const conversion = (
    <div className="mt-2 rounded-md bg-stone-50 px-2.5 py-2 text-center text-[11px] leading-relaxed text-stone-600 dark:bg-stone-800/60 dark:text-stone-300">
      <span className="font-mono">{usd(result.employeePremium)}/mo</span> × 12 ÷ {periods} ={" "}
      <strong className="font-mono text-stone-900 dark:text-stone-100">{usd(perCycle)}</strong> per {cycleLabel}
    </div>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Premium breakdown"
          title="How is this calculated?"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-stone-200 text-stone-400 transition-colors hover:text-stone-600 data-[state=open]:border-stone-900 data-[state=open]:bg-stone-900 data-[state=open]:text-white dark:border-stone-700 dark:hover:text-stone-200"
        >
          <Info className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
        className={(isRoster ? "w-96" : "w-80") + " p-0 text-[12px]"}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pt-3">
          <Info className="h-3.5 w-3.5 text-stone-500" />
          <span className="text-[12.5px] font-bold tracking-tight text-stone-900 dark:text-stone-100">
            Premium breakdown
          </span>
          <div className="flex-1" />
          <PopoverClose
            className="inline-flex h-5 w-5 items-center justify-center rounded text-stone-400 hover:text-stone-600 dark:hover:text-stone-200"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </PopoverClose>
        </div>
        <div className="px-4 pb-1 pt-1 text-[11px] text-stone-400">
          {planName}
          {coverageTier ? ` · ${tierLabel(coverageTier)}` : ""}
        </div>

        <div className="px-4 pb-4 pt-1">
          {isLoading ? (
            <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2.5 text-[12px] leading-relaxed text-stone-500 dark:border-stone-700 dark:bg-stone-800/60 dark:text-stone-400">
              Calculating premium…
            </div>
          ) : cannotCalculate ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              This plan {configMessage(needsConfig)} before per-paycheck costs can be calculated.
            </div>
          ) : employerPaid ? (
            <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2.5 text-[12px] leading-relaxed text-stone-600 dark:border-stone-700 dark:bg-stone-800/60 dark:text-stone-300">
              This plan is <strong className="text-stone-900 dark:text-stone-100">fully employer-paid</strong> — no
              payroll deduction for the employee.
            </div>
          ) : isRoster ? (
            <RosterView
              result={result}
              members={members}
              employee={employee}
              coveredDependents={coveredDependents ?? []}
              conversion={conversion}
              employerPerCycleText={employerPerCycleText}
            />
          ) : (
            <TierView result={result} conversion={conversion} employerPerCycleText={employerPerCycleText} />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RosterView({
  result,
  members,
  employee,
  coveredDependents,
  conversion,
  employerPerCycleText,
}: {
  result: EnrollmentCalculationResult;
  members: MemberBreakdown[];
  employee: any;
  coveredDependents: any[];
  conversion: React.ReactNode;
  employerPerCycleText: string;
}) {
  const names = resolveNames(members, employee, coveredDependents);
  const anyCapped = members.some((m) => m.capped);
  const cols = "grid grid-cols-[1.4fr_0.9fr_0.9fr_0.9fr] gap-2";

  return (
    <div>
      <div className={cols + " border-b border-stone-200 pb-1.5 text-[9.5px] font-bold uppercase tracking-wide text-stone-400 dark:border-stone-700"}>
        <span>Member</span>
        <span className="text-right">Rate</span>
        <span className="text-right">Employer</span>
        <span className="text-right">Employee</span>
      </div>

      <div className="max-h-[320px] overflow-y-auto">
        {members.map((m, i) => {
          const sub = [m.age != null ? `age ${m.age}` : null, m.ageBandLabel ? `band ${m.ageBandLabel}` : null]
            .filter(Boolean)
            .join(" · ");
          return (
            <div
              key={i}
              className={cols + " items-center border-b border-stone-100 py-1.5 dark:border-stone-800" + (m.capped ? " opacity-[0.55]" : "")}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[12px] font-semibold text-stone-900 dark:text-stone-100">{names[i]}</span>
                  {m.capped && (
                    <span className="shrink-0 rounded bg-amber-100 px-1 text-[8.5px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                      ACA $0
                    </span>
                  )}
                </div>
                {sub && <div className="text-[10px] text-stone-400">{sub}</div>}
              </div>
              <span className={"text-right font-mono text-[11.5px] font-semibold " + (m.capped ? "text-stone-400" : "text-stone-900 dark:text-stone-100")}>
                {usd(m.monthlyRate)}
              </span>
              <span className="text-right font-mono text-[11.5px] text-stone-400">{usd(m.employerAmount)}</span>
              <span className={"text-right font-mono text-[11.5px] font-semibold " + (m.capped ? "text-stone-400" : "text-stone-900 dark:text-stone-100")}>
                {usd(m.employeeAmount)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-2 border-t border-stone-200 pt-2 dark:border-stone-700">
        <TotalLine label="Total rate" value={`${usd(result.totalMonthlyRate)}/mo`} muted />
        <TotalLine label="Employer" value={`${usd(result.employerPremium)}/mo${employerPerCycleText}`} muted />
        <TotalLine label="Employee" value={`${usd(result.employeePremium)}/mo`} strong />
      </div>

      {conversion}

      {anyCapped && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10.5px] leading-relaxed text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <strong>ACA cap:</strong> only the 3 oldest children under age 21 are billable. Additional under-21 children
          are covered at $0.
        </div>
      )}
    </div>
  );
}

function TierView({
  result,
  conversion,
  employerPerCycleText,
}: {
  result: EnrollmentCalculationResult;
  conversion: React.ReactNode;
  employerPerCycleText: string;
}) {
  const tb = result.tierBreakdown;
  const pct = result.contributionResolved?.percentage;
  const employerPctText = pct != null ? `${Math.round(pct)}%` : null;
  const hasDeps = !!tb && tb.dependentPortion > 0;

  return (
    <div>
      <BreakdownLine label="Gross premium" value={`${usd(result.totalMonthlyRate)}/mo`} strong />
      {employerPctText && (
        <div className="border-b border-stone-200 pb-2 pt-0.5 text-[10.5px] leading-relaxed text-stone-400 dark:border-stone-700">
          Employer covers <strong className="text-stone-600 dark:text-stone-300">{employerPctText}</strong> of this plan.
        </div>
      )}
      {tb && (
        <>
          <BreakdownLine label="Employee-only portion" value={usd(tb.employeeOnlyRate)} />
          {hasDeps && <BreakdownLine label="Dependent portion" value={usd(tb.dependentPortion)} />}
        </>
      )}
      <div className="mt-1.5 border-t border-stone-200 pt-1 dark:border-stone-700">
        <BreakdownLine label="Employee total" value={`${usd(result.employeePremium)}/mo`} strong />
      </div>
      {conversion}
      <div className="mt-2 rounded-md bg-stone-50 px-2.5 py-2 text-[10.5px] leading-relaxed text-stone-500 dark:bg-stone-800/60 dark:text-stone-400">
        Employer pays <span className="font-mono text-stone-600 dark:text-stone-300">{usd(result.employerPremium)}/mo{employerPerCycleText}</span>{" "}
        of this plan's {usd(result.totalMonthlyRate)} premium.
      </div>
    </div>
  );
}

function BreakdownLine({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline gap-2.5 py-1">
      <span className={"shrink-0 text-[11.5px] " + (strong ? "font-semibold text-stone-900 dark:text-stone-100" : "text-stone-600 dark:text-stone-300")}>
        {label}
      </span>
      <span className="flex-1 -translate-y-0.5 border-b border-dotted border-stone-200 dark:border-stone-700" />
      <span className={"shrink-0 text-right font-mono " + (strong ? "text-[13px] font-bold text-stone-900 dark:text-stone-100" : "text-[12px] font-medium text-stone-600 dark:text-stone-300")}>
        {value}
      </span>
    </div>
  );
}

function TotalLine({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className={(strong ? "text-[12.5px] font-bold text-stone-900 dark:text-stone-100" : muted ? "text-[11.5px] text-stone-600 dark:text-stone-300" : "text-[11.5px] text-stone-900")}>
        {label}
      </span>
      <span className={"font-mono " + (strong ? "text-[13px] font-bold text-stone-900 dark:text-stone-100" : "text-[12px] text-stone-400")}>
        {value}
      </span>
    </div>
  );
}
